<template>
	<div class="main top-notch" id="agsat-app" style="overflow: hidden; display: flex; flex-direction: column;">
		<!-- TOP HEADER THAT INCLUDES LOGO, SEARCH, AND SETTINGS BUTTON -->
		<Header :a="this.$refs.mapView" :user='user' :mobile-view-port="mobileViewPort"/>

		<b-button-toolbar class="d-flex justify-content-md-start">
      <GmailLogin  :mobile-view-port="mobileViewPort" @clearMap="clearMap"/>
			<!-- BUTTONS TOOLBAR THAT ARE USED TO START DRAWING YOUR FIELD, EDIT AND DELETE FIELD -->
			<ButtonsToolbar
        @callMethodInFieldEdit="handleMethodCall"
        @saveEditedField="saveEditedField"
        ref="buttonToolbar"
        @showLoginModal="showLoginModal"
        @closeInfoWindow="closeInfoWindow"
        @startDrawing="startDrawing"
        :field="activeField"
        :mobile-view-port="mobileViewPort"
      />
			<!-- CONTROL PANEL THAT IS DISPLAYED ON MAP UNDER THE HEADER IT INCLUDES REALISATION, GUIDE AND LOGIN BUTTON -->
      <ControlPanel
        :user="user"
        :map="map"
        :mobile-view-port="mobileViewPort"
        @goToLocation="goToLocation"
        @goToDetectedLocation="goToDetectedLocation"
        @clearMap="clearMap"
        @measureFieldArea="measureFieldArea"
        @showGuideResults="showGuideResults"
        @hideGuideResults="hideGuideResults"
      />
      <!-- BUTTONS USED FOR DETECT LOCATION, ZOOM IN AND ZOOM OUT ON MOBILE -->
			<MobileZoomControlButtons />
		</b-button-toolbar>

    <!-- MAP VIEW -->
		<MapView @map-loaded="handleMapLoaded" :mobile-view-port="mobileViewPort" ref="mapView" @showLoginModal="showLoginModal" @fetchFieldIrrigation="fetchAndDisplayFieldIrrigation"/>

		<!-- Floating "Save Field Shape" button shown while reshaping a draft field
		after the >24h alert. Triggers the irrigation re-check on demand so the
		backend isn't called on every vertex drag. -->
		<b-button
			v-if="draftEditing"
			class="save-shape-btn"
			variant="success"
			@click="saveDraftShape">
			<b-icon icon="check2"></b-icon>
			{{ $t('global.save_field_shape') }}
		</b-button>

		<!-- FIELDINDEX CONTAINS A LIST OF ALL FIELDS FETCHED FOR THE CURRENT USER -->
		<FieldIndex :mobile-view-port="mobileViewPort" :current-fields="fields" @delete-field="handleDeleteField" @goToField="handleGoToField"/>
  <!-- FIELDNEW CONTAINS THE MODAL FOR A NEW FIELD -->
		<FieldNew :field="draftField" :fields="fields" />
		<!-- FIELDEDIT IS THE SAME AS THE FIELDNEW BUT POPULATED WITH THE SELECTED FIELD DETAILS -->
		<FieldEdit v-if="activeField" @goToField="handleGoToField" @closeInfoWindow="closeInfoWindow" ref="fieldEdit" :field="activeField"/>
		<!-- SOCIAL MEDIA SHARE MODAL -->
		<SocialMediaShareModal />

		<!-- CROP TYPE MODAL USED BY THE USER TO SELECT THE CROP TYPE FOR HIS FIELD-->
		<CropTypeModal @clearDraftSelection="clearDraftSelection" @showIrrigationModal="showIrrigationModal" />
    <!-- IRRIGATION SYSTEM MODAL USED BY THE USER TO SELECT THE IRRIGATION SYSTEM USED FOR HIS FIELD-->
		<IrrigationSystemModal @clearDraftSelection="clearDraftSelection" @showPipeSizeModal="showPipeSizeModal"/>
		<!-- PIPE SIZE  MODAL USED BY THE USER TO SELECT THE PIPE SIZE USED FOR HIS FIELD-->
    <PipeSizeModal :mobile-view-port="mobileViewPort" @clearDraftSelection="clearDraftSelection" @startDrawing="startDrawing" @pipeSizeConfirmed="onPipeSizeConfirmed" />
		<!--MODAL CALLED WHEN IRRIFATION SYSTEM EXCEEDS 24 HOURS -->
		<IrrigationTimeExceededModal @retryWithNewPipeSize="retryWithNewPipeSize" @redrawField="redrawFromAlert" @cancelDraft="clearDraftPolygon"/>
    <LoginRequiredModal />
    <FieldAreaModal :fieldArea="area"/>

		<!--SIDEBAR WHICH INCLUDES ALL FIELDS DISPLAYED ONLY FOR ADMINS -->
		<!-- <AllUsersSidebar :fields="fields" v-on:childToParent="filterFieldByUserSelection" /> -->
		<!-- DETAILED FIELD INFO FOR THE SELECTED FIELD -->
		<!-- <SidebarInfo :selected-field="activeField" /> -->

		<!-- RESULTS TAB AND HISTORY CHART -->
    <ResultsTab ref="resultsTab" :mobile-view-port="mobileViewPort" :field="activeField || draftField"/>
		<!-- <ResultsTab :results-object="res"
			:hide-tabs="hideTabs"
			:fetching-data="fetchingData"
			:series="series"
			:options="options"
			@fetchDataChanged="(e) => { stopFetchingData = e }"
			@hideTabsChanged="(e) => { hideTabs = e }"
    /> -->

		<!-- THE HELP TEXT DIV THAT SHOWS ON MOBILE WHEN USER IS NOT LOGGED IN AND DOESNT HAVE ANY SAVED FIELDS -->
		<!-- <HelpText /> -->
	</div>
</template>
<script>
import { escapeHtml } from '../../utils/escapeHtml';
import Header from './Header';
import ControlPanel from './ControlPanel';
import ButtonsToolbar from './ButtonsToolbar';
import MobileZoomControlButtons from './MobileZoomControlButtons';
import FieldIndex from '../Field/FieldIndex';
import FieldNew from '../Field/FieldNew';
import FieldEdit from '../Field/FieldEdit';
import SocialMediaShareModal from './SocialMediaShareModal';
import MapView from './MapView';
import GmailLogin from './GmailLogin';
import CropTypeModal from './CropTypeModal';
import LoginRequiredModal from './LoginRequiredModal';
import IrrigationSystemModal from './IrrigationSystemModal';
import PipeSizeModal from './PipeSizeModal';
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { closePolygonRing, normalizeOpenPolygonRing } from '../../utils/polygonCoordinates';

import IrrigationTimeExceededModal from './IrrigationTimeExceededModal';
import FieldAreaModal from './FieldAreaModal';
import * as firebase from '../../firebase';
// import AllUsersSidebar from './AllUsersSidebar';
// import SidebarInfo from './SidebarInfo';
import ResultsTab from './ResultsTab';
import { WEATHER_ENABLED } from '../../config/features';
// import HelpText from './HelpText';
import { mapState, mapGetters } from 'vuex';
import { getPolygonValue, warmBackend } from '../../services/UserService';

const ET_REQUEST_TIMEOUT_MS = 60000;

export default {
	name: 'Dashboard',

	components: {
		Header,
		ControlPanel,
		ButtonsToolbar,
		MobileZoomControlButtons,
		FieldIndex,
		FieldNew,
		FieldEdit,
		SocialMediaShareModal,
		MapView,
    GmailLogin,
    LoginRequiredModal,
		CropTypeModal,
		IrrigationSystemModal,
		PipeSizeModal,
		IrrigationTimeExceededModal,
		ResultsTab,
    FieldAreaModal
	},

	data() {
		return {
      windowWidth: window.innerWidth,
      map: null,
      area: 0,
      pipeSizeRatio: null,
      pipeSizes:[
				"32 mm, 1 inch",
				"40 mm, 1.25 inch",
				"50 mm, 1.5 inch",
				"63 mm, 2 inch",
				"75 mm, 2.5 inch",
				"90 mm, 3 inch",
				"110 mm, 4 inch",
				"140 mm, 5 inch",
				"160 mm, 6 inch"
			],
      irrigationSystemTypes: [
        'Drip',
        'Bubbler',
        'Microsprinkler',
        'Solid-set impact sprinkler',
        'Center Pivot',
        'Linear move',
        'Hand-move sprinkler',
        'Boom sprayer',
        'Gun',
        'Surface-Basin',
        'Surface-Furrow'
      ],
      irrigationRatio: 1,
      currentPolygon: null,
      terraDraw: null,
      terraDrawAdapter: null,
      drawingMode: null,
      retryingWithPolygon: false,
      suppressNextStartDrawing: false,
      draftEditing: false,
      // Monotonic token so a slow earlier irrigation request can't be revived
      // by a newer one and overwrite/stall the latest result.
      irrigationRequestId: 0
		}
	},

  watch: {
    // Rebuild the field info window in the new language. The window content is
    // pre-rendered HTML (built with $i18n at compute time), so it must be
    // regenerated on a locale switch — otherwise it keeps the old language and,
    // via MapView's basic fallback, showed the raw m² area instead of hectares.
    '$i18n.locale': function () {
      this.rebuildInfoWindowForLocale();
    }
  },

  created () {
    this.goToDetectedLocation();
    // eslint-disable-next-line
    const user = localStorage.getItem('user');
    if (user) {
      this.$store.dispatch('login', JSON.parse(user));
    }
    firebase.firebase.auth().onAuthStateChanged(user => {
      if (user) {
        this.$store.dispatch('login', user);
        this.$store.dispatch('fetchFieldsByDevice', user.email || user.phoneNumber);
      } else {
        this.$store.dispatch('logout');
      }
    });

    this.$root.$on('goToField', this.handleGoToField);
  },

  mounted () {
    warmBackend();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.onKeydown);
  },



  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.onKeydown);
    if (this.terraDraw) {
      this.terraDraw.stop();
    }
  },

	methods: {
    showGuideResults(tabIndex) {
      if (this.$refs.resultsTab) {
        this.$refs.resultsTab.showGuideTab(tabIndex);
      }
    },

    hideGuideResults() {
      if (this.$refs.resultsTab) {
        this.$refs.resultsTab.restoreAfterGuide();
      }
    },

    handleMapLoaded(mapComponent) {
      this.map = mapComponent;
      this.initializeTerraDrawTool(mapComponent);
    },

    initializeTerraDrawTool (mapComponent) {
      if (this.terraDraw) {
        return;
      }
      const gmapRef = mapComponent || (this.$refs.mapView && this.$refs.mapView.$refs.googleMap);
      if (!gmapRef) {
        return;
      }
      gmapRef.$mapPromise.then((map) => {
        const input = document.getElementById("place-search");
        new window.google.maps.places.Autocomplete(input);

        const adapter = new TerraDrawGoogleMapsAdapter({
          lib: window.google.maps,
          map: map,
          coordinatePrecision: 6
        });
        // TerraDraw adapter expects the interactive map layer element; fall back safely
        adapter.getMapEventElement = () => {
          const candidate = map.getDiv().querySelector('div[style*="z-index: 3;"]');
          return candidate || map.getDiv();
        };

        this.terraDrawAdapter = adapter;
        this.terraDraw = new TerraDraw({
          adapter,
          modes: [
            new TerraDrawPolygonMode({
              pointerDistance: 5
            }),
            new TerraDrawSelectMode()
          ],
        });

        this.terraDraw.on('finish', this.handleTerraDrawFinish);
        this.terraDraw.start();
      });
    },

    async goToDetectedLocation () {
      navigator.geolocation.getCurrentPosition(function(position) {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        this.goToLocation(lat, lng);
        this.addCurrentLocationMarker(lat, lng);
      }.bind(this));
    },

    handleGoToField (fieldInfo) {
      this.goToLocation(fieldInfo.markerLatitude, fieldInfo.markerLongitude, 18);
    },

    handleMethodCall() {
      this.$refs.fieldEdit.editFieldDrawing();
    },
    saveEditedField() {
      if (this.$refs.fieldEdit && this.activeField) {
        this.$refs.fieldEdit.editFormSubmitted();
      }
    },

    addCurrentLocationMarker(latitude, longitude) {
      this.$refs.mapView.$refs.googleMap.$mapPromise.then((map) => {
        // Create a new marker
        const marker = new window.google.maps.Marker({
          position: { lat: latitude, lng: longitude },
          map: map
        });

        // Create a new info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: '<b>Current Location</b>'
        });

        infoWindow.open(map, marker);

        // Add a click event listener to the marker to open the info window
        marker.addListener('click', function() {
          infoWindow.open(map, marker);
        });
      });
    },

    goToLocation (lat=null, lng=null, zoomLevel=17) {
      this.$refs.mapView.$refs.googleMap.$mapPromise.then((map) => {
        if (lat && lng) {
          const position = {lat: lat,lng: lng};
          map.setCenter(position);
          map.setZoom(zoomLevel);
        }
      });
    },

    removeFieldPolygon (polygon) {
      this.$store.commit('removeMapPolygon', polygon);
    },

    closeInfoWindow() {
      this.$refs.mapView.closeInfoWindow();
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.stopDrawing();
      }
    },

    stopDrawing() {
      if (this.terraDraw) {
        this.terraDraw.clear();
        this.terraDraw.setMode('static');
      }
      this.drawingMode = null;
    },

    startDrawing () {
      if (this.suppressNextStartDrawing) {
        this.suppressNextStartDrawing = false;
        return;
      }
      warmBackend();
      // Remove a leftover DRAFT polygon graphic (e.g. starting a fresh draw
      // mid-reshape) so the old shape doesn't linger. Never touch a saved
      // field's polygon (those carry `saved === true`).
      const stale = this.currentPolygon || (this.activePolygon && !this.activePolygon.saved ? this.activePolygon : null);
      if (stale && stale.setMap) {
        stale.setMap(null);
      }
      this.currentPolygon = null;
      this.draftEditing = false;
      // Clear any stale selection so toolbar buttons don't show a phantom
      // field while a fresh draw is in progress.
      this.$store.commit('setActivePolygon', null);
      this.drawingMode = 'field';
      if (this.terraDraw) {
        this.terraDraw.clear();
        this.terraDraw.setMode('polygon');
      }
    },

    measureFieldArea () {
      this.currentPolygon = null;
      this.drawingMode = 'measure';
      if (this.terraDraw) {
        this.terraDraw.clear();
        this.terraDraw.setMode('polygon');
      }
    },

    handleTerraDrawFinish(id, context) {
      if (!this.terraDraw || context.mode !== 'polygon') {
        return;
      }
      const feature = this.terraDraw.getSnapshotFeature(id);
      if (!feature || feature.geometry.type !== 'Polygon') {
        return;
      }

      const path = normalizeOpenPolygonRing(feature.geometry.coordinates[0])
        .map(([lng, lat]) => ({ lat, lng }));
      if (path.length < 3) {
        this.terraDraw.removeFeatures([id]);
        this.drawingMode = null;
        this.terraDraw.setMode('static');
        return;
      }
      const polygon = new window.google.maps.Polygon({
        paths: path,
        strokeColor: "#8B4513",
        fillColor: "#98FB98",
        editable: false
      });

      this.terraDraw.removeFeatures([id]);
      this.$refs.mapView.$refs.googleMap.$mapPromise.then((map) => {
        polygon.setMap(map);
      });

      if (this.drawingMode === 'measure') {
        this.onMeasurePolygonComplete(polygon);
      } else if (this.drawingMode === 'field') {
        this.onFieldPolygonComplete(polygon);
      }

      this.drawingMode = null;
      this.terraDraw.setMode('static');
    },

    onFieldPolygonComplete(polygon) {
      this.$store.commit('setFetchingResults', true);
      const computedArea = window.google.maps.geometry.spherical.computeArea(polygon.getPath());
      const fieldAttributes = {
        pipeSize: this.$store.state.draftPipeSize,
        irrigationSystem: this.$store.state.draftIrrigationSystem,
        cropType: this.$store.state.draftCrop,
        fieldArea: parseFloat(computedArea).toFixed(2),
        position: (this.formatPolygonCoordinates(this.getPolygonCoordinates(polygon.getPath()))).join(','),
        markerLatitude: this.getPolygonCentroid(polygon).latitude,
        markerLongitude: this.getPolygonCentroid(polygon).longitude,
      };
      this.currentPolygon = polygon;
      this.fetchAndDisplayFieldIrrigation(polygon);
      this.addMarker(polygon);
      polygon.setEditable(false);
      this.$store.commit('setDraftField', fieldAttributes);
    },

    onMeasurePolygonComplete(polygon) {
      const computedArea = window.google.maps.geometry.spherical.computeArea(polygon.getPath());
      this.area = parseFloat(computedArea).toFixed(2);
      if (this.area > 0) {
        this.$bvModal.show('field-area');
      }

      setTimeout(() => {
        polygon.setMap(null);
      }, 3500);
    },

    fetchAndDisplayFieldIrrigation(polygon) {
      this.$store.commit('setFetchingResults', true);
      this.getPolygonIrrigationResult(polygon);
      this.$store.commit('setActivePolygon', polygon);
      if (WEATHER_ENABLED) {
        this.fetchWeatherForPolygon(polygon);
      }
    },

    fetchWeatherForPolygon(polygon) {
      // Weather is best-effort and independent of the ET result. Use the
      // field centroid for the forecast lookup.
      // Clear any prior field's forecast up front so we never momentarily
      // show stale weather for the newly selected field.
      this.$store.commit('setWeather', null);
      this.$store.commit('setWeatherError', null);
      try {
        const centroid = this.getPolygonCentroid(polygon);
        if (centroid && isFinite(centroid.latitude) && isFinite(centroid.longitude)) {
          this.$store.dispatch('fetchWeather', {
            lat: centroid.latitude,
            lng: centroid.longitude
          });
        }
      } catch (e) {
        // Never let a weather lookup failure affect the irrigation flow.
        console.warn('Weather fetch skipped:', e);
      }
    },

    getPolygonCentroid (polygon) {
      let path = polygon.getPath();
      let lat = 0;
      let lng = 0;
      for (let i = 0; i < path.getLength(); i++) {
        lat += path.getAt(i).lat();
        lng += path.getAt(i).lng();
      }
      lat /= path.getLength();
      lng /= path.getLength();
      return {latitude: lat, longitude: lng};
    },

    addMarker (polygon) {
      let self = this;
      this.$refs.mapView.$refs.googleMap.$mapPromise.then((map) => {
        // Create a new marker
        let centroid = this.getPolygonCentroid(polygon);
        let marker = new window.google.maps.Marker({
          position: { lat: centroid.latitude, lng: centroid.longitude },
          map: map
        });

        self.$store.commit('setActiveMarker', marker);
      });
    },

    getPolygonIrrigationResult (polygon) {
      // Token for THIS request; any later request bumps it, so a slow earlier
      // response is dropped instead of overwriting or stalling the latest one.
      const reqId = ++this.irrigationRequestId;
      this.$store.commit('setResults', null);
      this.$store.commit('setShouldIgnoreResult', false);
      this.$store.commit('setIrrigationResult', '');
      let coordinates = this.getPolygonCoordinates(polygon.getPath());
      let geojson = this.getGeoJson(coordinates);

      let self = this;
      let apiCall = getPolygonValue(geojson);
      let timeout = new Promise((resolve, reject) => {
        setTimeout(() => reject(new Error('Request timed out')), ET_REQUEST_TIMEOUT_MS);
      });

      Promise.race([apiCall, timeout])
        .then(response => {
          if (reqId !== self.irrigationRequestId) return; // superseded by a newer request
          // Transient server error (5xx / 4xx): show a "try again" message, NOT
          // the "no satellite data" one — the field is fine, the backend hiccuped
          // (e.g. a 504 evaluate-deadline or a cold-start 500). A 504 reads as a
          // timeout; anything else reads as the service being momentarily busy.
          if (response && response.httpStatus) {
            self.$store.commit('setFetchingResults', false);
            self.$store.commit('setResults', []);
            const busyMsg = response.httpStatus === 504
              ? this.$i18n.t('global.request_timed_out')
              : this.$i18n.t('global.service_busy');
            self.$store.commit('setIrrigationResult', busyMsg);
            self.$store.commit('setShouldIgnoreResult', true);
            return;
          }
          const noData =
            !response ||
            !Array.isArray(response) ||
            response.length === 0 ||
            response.every(item => !item || typeof item[0] === 'undefined' || item[0] === null);
          if (noData) {
            const msg = (this.$i18n && this.$i18n.te && this.$i18n.te('global.no_recent_satellite_data'))
              ? this.$i18n.t('global.no_recent_satellite_data')
              : 'No recent satellite data available for this area. Please try again later.';
            self.$store.commit('setFetchingResults', false);
            self.$store.commit('setResults', []);
            self.$store.commit('setIrrigationResult', msg);
            self.$store.commit('setShouldIgnoreResult', true);
            return;
          }
          self.$store.commit('setFetchingResults', false);
          let dailyValue = (parseFloat(response[response.length - 5][0]).toFixed(3) * 10.0)/this.getIrrigationRatio;
          let field = this.activeField || this.draftField;
          self.getPipeSizeRatio(field.pipeSize);
          // Compute with full precision and round only for display, so this
          // matches the per-day Irrigation Duration tab (which doesn't round
          // intermediate values). Rounding area/total first caused a 1.07 vs
          // 1.08 mismatch between the info window and the duration tab.
          const areaHa = parseFloat(field.fieldArea) / 10000;
          const totalRequirementRaw = areaHa * dailyValue;
          const irrigationDuration = (totalRequirementRaw / self.pipeSizeRatio).toFixed(2);
          const fieldAreaInHectare = areaHa.toFixed(2);
          const totalRequirement = totalRequirementRaw.toFixed(2);
          if (irrigationDuration > 24) {
            // Over the limit: this configuration is infeasible, so don't show
            // its results behind the alert. Clear them, close the results card,
            // and prompt the user to fix the pipe/field. The polygon stays
            // editable underneath.
            self.$store.commit('setResults', []);
            self.$store.commit('setIrrigationResult', '');
            self.$root.$emit('hideResultsCard');
            this.$bvModal.show('irrigation-duration-alert');
            self.$store.commit('setShouldIgnoreResult', true);
          } else {
            // Acceptable: publish the results for display. If we were reshaping
            // a draft, end the edit (lock corners, move marker to new centroid).
            self.$store.commit('setResults', response);
            if (self.draftEditing) {
              self.endDraftReshape();
            }
            let windownResult = self.setInfoWindowContent(dailyValue, fieldAreaInHectare, totalRequirement, irrigationDuration, field.name, field.cropType);
            self.$store.commit('setIrrigationResult', windownResult);
          }
        })
        .catch(error => {
          if (reqId !== self.irrigationRequestId) return; // superseded by a newer request
          if (error.message === 'Request timed out') {
            let windownResult = this.$i18n.t('global.request_timed_out');
            self.$store.commit('setFetchingResults', false);
            self.$store.commit('setResults', []);
            self.$store.commit('setIrrigationResult', windownResult);
          } else {
            console.log(error);
            self.$store.commit('setFetchingResults', false);
            self.$store.commit('setResults', []);
            const msg = (this.$i18n && this.$i18n.te && this.$i18n.te('global.no_recent_satellite_data'))
              ? this.$i18n.t('global.no_recent_satellite_data')
              : 'No recent satellite data available for this area. Please try again later.';
            self.$store.commit('setIrrigationResult', msg);
          }
          self.$store.commit('setShouldIgnoreResult', true);
        })
        .finally(() => {
          // Only the latest request may clear the spinner, so a stale request
          // finishing can't hide an in-flight newer one.
          if (reqId === self.irrigationRequestId) {
            self.$store.commit('setFetchingResults', false);
          }
        });
      // Call the API
      // getPolygonValue(geojson)
      //   .then(response => {
      //     if (self.shouldIgnoreResult){
      //       self.$store.commit('setFetchingResults', true);
      //       return;
      //     } else{
      //       self.$store.commit('setFetchingResults', false);
      //       self.$store.commit('setResults', response);
      //       let dailyValue = (parseFloat(response[response.length - 5][0]).toFixed(2) * 10.0)/this.getIrrigationRatio;
      //       let totalRequirement, fieldAreaInHectare;
      //       let irrigationDuration;
      //       let field = this.activeField || this.draftField;
      //       fieldAreaInHectare = (parseFloat(field.fieldArea/10000)).toFixed(2);
      //       totalRequirement = (fieldAreaInHectare * dailyValue).toFixed(2);
      //       self.getPipeSizeRatio(field.pipeSize);
      //       irrigationDuration = (totalRequirement/self.pipeSizeRatio).toFixed(2);
      //       let windownResult = self.setInfoWindowContent(dailyValue, fieldAreaInHectare, totalRequirement, irrigationDuration);
      //       self.$store.commit('setIrrigationResult', windownResult);
      //     }
      // })
      //   .catch(error => {
      //     console.log(error);
      // });
    },

    // Rebuild the info window in the current locale from the stored result, so
    // a language switch re-translates the full content (and keeps the area in
    // hectares) instead of falling back to a partial, untranslated window.
    rebuildInfoWindowForLocale() {
      const field = this.activeField || this.draftField;
      const result = this.$store.state.result;
      if (!field) return;
      // Only rebuild the full irrigation window when we have feasible results.
      if (!Array.isArray(result) || result.length === 0) return;
      const last = result[result.length - 5];
      if (!last || last[0] === null || typeof last[0] === 'undefined') return;

      const dailyValue = (parseFloat(last[0]).toFixed(3) * 10.0) / this.getIrrigationRatio;
      this.getPipeSizeRatio(field.pipeSize);
      const areaHa = parseFloat(field.fieldArea) / 10000;
      const totalRequirementRaw = areaHa * dailyValue;
      const irrigationDuration = (totalRequirementRaw / this.pipeSizeRatio).toFixed(2);
      if (irrigationDuration > 24) return; // infeasible: no window is shown
      const windownResult = this.setInfoWindowContent(
        dailyValue,
        areaHa.toFixed(2),
        totalRequirementRaw.toFixed(2),
        irrigationDuration,
        field.name,
        field.cropType
      );
      this.$store.commit('setIrrigationResult', windownResult);
    },

    setInfoWindowContent: function (value, fieldArea, totalRequirement, irrigationDuration, fieldName, cropType) {
        value = this.$i18n.n(value)
        fieldArea = this.$i18n.n(fieldArea)
      if (totalRequirement) {
        totalRequirement = this.$i18n.n(totalRequirement)
        irrigationDuration = this.$i18n.n(irrigationDuration)
        // Rendered through MapView's v-html info window, so user-controlled
        // values must be escaped here. See utils/escapeHtml.js.
        const safeFieldName = escapeHtml(fieldName);
        const cropLabel = cropType ? escapeHtml(this.$i18n.t(`crops.types.${cropType}`)) : '';
        return (
        `<div class="">
          <div>
            <div>
              ${safeFieldName ? `<div class="m-2"><span style="font-weight: bold;">${this.$i18n.t(`global.field_name`)}: </span>${safeFieldName}</div>` : ''}
              ${cropLabel ? `<div class="m-2"><span style="font-weight: bold;">${this.$i18n.t(`crops.crop_type`)}: </span>${cropLabel}</div>` : ''}
              <div class="m-2"><span style="font-weight: bold;">${this.$i18n.t(`global.field_area_in_hectare`)}: </span>
                ${fieldArea} ${this.$i18n.t(`global.hectare`)}
              </div>
              <div class="m-2"><span style="font-weight: bold;">${this.$i18n.t(`global.water_requirement_today`)}: </span>
                ${value} ${this.$i18n.t(`global.meter_cube_per_hectare`)}
              </div>
              <div class="m-2"><span style="font-weight: bold;">${this.$i18n.t(`global.total_requirement`)}: </span>
                ${totalRequirement} ${this.$i18n.t(`global.meter_cube_per_day`)}
              </div>
              <div class="m-2"><span style="font-weight: bold;">${this.$i18n.t(`global.irrigation_duration`)}: </span>
                ${irrigationDuration} ${this.$i18n.t(`global.hr_per_day`)}
              </div>
            </div>
          </div>
        </div>`);
      } else {
        return (
        `<div class="">
          <div>
            <div>
              <div class="m-2"><span style="font-weight: bold;">${this.$i18n.t(`global.crop_evapotranspiration`)}: </span>
                ${value} ${this.$i18n.t(`global.mm_per_day`)}
              </div>
            </div>
          </div>
        </div>`);
      }
    },

    getPolygonCoordinates (polygonPath) {
      const coordinates = polygonPath.getArray().map(point => [point.lng(), point.lat()]);
      return closePolygonRing(coordinates);
    },

    getGeoJson (coordinates) {
      // Create geojson object
      return {
        "type": "Polygon",
        "coordinates": [coordinates]
      };
    },

    formatPolygonCoordinates (coordinates) {
      return coordinates.map(coords => `lat/lng: (${coords[1]}, ${coords[0]})`);
    },

    handleDeleteField(fieldId) {
      this.$refs.buttonToolbar.deleteField(fieldId);
    },

    clearMap () {
      if (this.terraDraw) {
        this.terraDraw.clear();
      }
      // Loop over polygons
      this.mapPolygons.forEach(polygon => {
        // You can access each polygon here
        polygon.setMap(null);
      });

      // Loop over markers
      this.allMarkers.forEach(marker => {
        // You can access each marker here
        marker.setMap(null);
      });
    },


    getPipeSizeRatio (pipeSize) {
      switch(this.pipeSizes.indexOf(pipeSize)) {
        case 0:
          this.pipeSizeRatio = 2.6;
          break;
        case 1:
          this.pipeSizeRatio = 4.0;
          break;
        case 2:
          this.pipeSizeRatio = 6.0;
          break;
        case 3:
          this.pipeSizeRatio = 11.0;
          break;
        case 4:
          this.pipeSizeRatio = 17.0;
          break;
        case 5:
          this.pipeSizeRatio = 24.0;
          break;
        case 6:
          this.pipeSizeRatio = 42.0;
          break;
        case 7:
          this.pipeSizeRatio = 66.0;
          break;
        case 8:
          this.pipeSizeRatio = 90.0;
          break;
      }
    },

    showIrrigationModal () {
      this.$bvModal.show('irrigation-system-modal');
    },

    showPipeSizeModal() {
      this.$bvModal.show('pipe-size-modal');
    },

    clearDraftPolygon() {
      // Remove the drawn polygon from the map. Cover every reference that may
      // point at it (Vuex activePolygon and the local currentPolygon) so a
      // redraw can't leave the old shape behind.
      if (this.activePolygon) {
        this.activePolygon.setMap(null);
        this.$store.commit('setActivePolygon', null);
      }
      if (this.currentPolygon) {
        this.currentPolygon.setMap(null);
        this.currentPolygon = null;
      }
      if (this.activeMarker) {
        this.activeMarker.setMap(null);
        this.$store.commit('setActiveMarker', null);
      }
      // Also drop any in-progress TerraDraw feature so nothing is left mid-draw.
      if (this.terraDraw) {
        this.terraDraw.clear();
      }
      this.$store.commit('setDraftField', null);
      this.$store.commit('setResults', null);
      this.$store.commit('setShouldIgnoreResult', false);
      this.$store.commit('setWeather', null);
      this.$store.commit('setWeatherError', null);
      this.retryingWithPolygon = false;
      this.draftEditing = false;
    },

    redrawFromAlert() {
      // Make the existing draft polygon editable (draggable corners) so the
      // user can shrink/reshape it, rather than discarding it and starting a
      // fresh draw. The backend re-check is NOT auto-triggered on every vertex
      // move — the user reshapes freely and then presses "Save Field Shape".
      const polygon = this.activePolygon || this.currentPolygon;
      if (!polygon) {
        // Nothing to edit (shouldn't happen for a draft) — fall back to draw.
        this.clearDraftPolygon();
        this.startDrawing();
        return;
      }
      // Note: we deliberately do NOT set the global editMode here. That flag
      // drives the saved-field Save/Cancel toolbar flow which assumes an
      // activeField with a key; a draft has neither.
      this.draftEditing = true;
      this.$store.commit('setShouldIgnoreResult', true);
      polygon.setEditable(true);
    },

    // Triggered by the "Save Field Shape" button. Recomputes the draft field's
    // geometry from the edited polygon and re-runs the irrigation check once.
    saveDraftShape() {
      const polygon = this.activePolygon || this.currentPolygon;
      if (!polygon) return;
      const computedArea = window.google.maps.geometry.spherical.computeArea(polygon.getPath());
      const updatedDraft = {
        ...(this.draftField || {}),
        fieldArea: parseFloat(computedArea).toFixed(2),
        position: (this.formatPolygonCoordinates(this.getPolygonCoordinates(polygon.getPath()))).join(','),
        markerLatitude: this.getPolygonCentroid(polygon).latitude,
        markerLongitude: this.getPolygonCentroid(polygon).longitude
      };
      this.$store.commit('setDraftField', updatedDraft);
      this.$store.commit('setActivePolygon', polygon);
      // Keep the centroid marker in sync with the reshaped polygon.
      if (this.activeMarker) {
        this.activeMarker.setPosition({
          lat: updatedDraft.markerLatitude,
          lng: updatedDraft.markerLongitude
        });
      }
      this.$store.commit('setShouldIgnoreResult', false);
      this.fetchAndDisplayFieldIrrigation(polygon);
    },

    // Ends a draft reshape session: lock the polygon's corners. Called once a
    // reshaped draft is within the 24h limit (or the user otherwise exits).
    endDraftReshape() {
      this.draftEditing = false;
      const polygon = this.activePolygon || this.currentPolygon;
      if (polygon) {
        polygon.setEditable(false);
      }
    },

    retryWithNewPipeSize() {
      if (!this.activePolygon) return;
      this.retryingWithPolygon = true;
      this.$bvModal.show('pipe-size-modal');
    },

    onPipeSizeConfirmed() {
      if (this.retryingWithPolygon && this.activePolygon) {
        this.retryingWithPolygon = false;
        // PipeSizeModal emits `startDrawing` right after this; suppress that
        // single call so the existing polygon is reused instead of cleared.
        this.suppressNextStartDrawing = true;
        // Sync the newly chosen pipe size into the draft field so the
        // duration is recalculated with the updated value, not the old one.
        if (this.draftField) {
          this.$store.commit('setDraftField', {
            ...this.draftField,
            pipeSize: this.$store.state.draftPipeSize
          });
        }
        this.$store.commit('setShouldIgnoreResult', false);
        this.fetchAndDisplayFieldIrrigation(this.activePolygon);
      }
    },

    showLoginModal () {
      if (!this.userGuideMode) {
        this.$bvModal.show('login-required-modal');
      }
    },

    clearDraftSelection () {
      // If the user cancelled the pipe-size modal during a retry, drop the
      // retry flag so it doesn't leak into the next new-field flow.
      this.retryingWithPolygon = false;
      this.$store.dispatch('clearDraftSelection');
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
	},

	computed: {
		...mapState(['user', 'fields', 'activeField', 'activePolygon', 'mapPolygons', 'allMarkers', 'activeMarker', 'shouldIgnoreResult', 'draftField', 'irrigationWindow', 'userGuideMode']),
    ...mapGetters(['getIrrigationRatio']),
		mobileViewPort () {
			return this.windowWidth < 768;
		}
	}
}
</script>

<style lang="scss" scoped>
// Floating action button for confirming a draft field reshape.
.save-shape-btn {
  position: fixed;
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  padding: 0.6rem 1.4rem;
  border-radius: 2rem;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}
</style>
